(function() {
  'use strict';

  // ========================================
  // 定数定義
  // ========================================
  const SKU_PARAM_KEY = 'sku';
  const TEMPLATE_PARAM_KEY = 'template';
  const PRODUCT_PARAM_KEY = 'product';
  const VIEW_PARAM_KEY = 'view';
  const VARIANT_OPTIONS_PARAM_KEY = 'variant_options';
  const SELECTOR_PRODUCT_SKU = '.product-sku';
  const SELECTOR_TEMPLATE_SKU = '.template-sku';
  const SELECTOR_STEPPER = '[data-stepper]';
  const SELECTOR_STEP_TEMPLATE = '.step-template';
  const SELECTOR_SELECTION_INFO = '[data-selection-info]';
  const ATTR_SKIP_PARAM = 'data-skip-product-param';
  const ID_CREATOR_COLLECTION_LIST = 'creator-collection-list';
  const VIEW_STEP_CREATOR_PRODUCTS = 'step-creator-products';
  const PARAM_KEYS_TO_SKIP_IN_EDITOR = new Set([
    SKU_PARAM_KEY,
    TEMPLATE_PARAM_KEY,
    PRODUCT_PARAM_KEY,
    VIEW_PARAM_KEY,
    VARIANT_OPTIONS_PARAM_KEY,
  ]);

  // ========================================
  // 内部ステート
  // ========================================
  // ステップフローページ全体で共有する現在の商品SKU/テンプレートSKU/商品名/バリアントオプション
  // URLが外部で書き換えられても、この内部ステートを信頼のソースとして使用
  let currentSku = '';
  let currentTemplate = '';
  let currentProduct = '';
  let currentVariantOptions = '';

  // バリアント変更処理の重複実行を防ぐフラグ
  let isProcessingVariantChange = false;

  // ========================================
  // ユーティリティ関数
  // ========================================

  // URL文字列を構築（重複コードの削減）
  function buildUrlString(url) {
    const search = url.searchParams.toString();
    return url.pathname + (search ? '?' + search : '') + url.hash;
  }

  // 相対URLか絶対URLかを判定して返す
  function formatUrl(url, originalHref) {
    try {
      const urlString = buildUrlString(url);
      return url.origin === window.location.origin ? urlString : url.origin + urlString;
    } catch (e) {
      return originalHref;
    }
  }

  // パラメータ値を正規化（文字列のtrim、それ以外は空文字）
  function normalizeParam(value) {
    return (typeof value === 'string') ? value.trim() : '';
  }

  // バリアントオブジェクトからオプション値を取得して文字列に変換
  function getVariantOptionsString(variant) {
    if (!variant) return '';
    const options = [];
    if (variant.option1) options.push(variant.option1);
    if (variant.option2) options.push(variant.option2);
    if (variant.option3) options.push(variant.option3);
    return options.join(' / ');
  }

  // ========================================
  // DOM操作とデータ取得
  // ========================================

  // テキスト値の正規化を行い、不要な空白やSKUプレフィックスを取り除く
  function extractNormalizedSku(raw, { stripSkuPrefix = false } = {}) {
    let normalized = raw.replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    if (stripSkuPrefix) {
      normalized = normalized.replace(/^SKU:\s*/i, '').trim();
    }
    return normalized;
  }

  // 指定セレクタからテキストノードを抽出し、純粋な文字列を取得する
  function extractRawText(selector) {
    const element = document.querySelector(selector);
    if (!element) return '';
    const textNode = Array.from(element.childNodes || []).find((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return false;
      return node.textContent && node.textContent.trim();
    });
    return textNode ? textNode.textContent : (element.textContent || '');
  }

  // 商品SKU表示から値を取得
  function getProductSkuFromDom() {
    const raw = extractRawText(SELECTOR_PRODUCT_SKU);
    return extractNormalizedSku(raw, { stripSkuPrefix: true });
  }

  // テンプレートSKU表示から値を取得
  function getTemplateSkuFromDom() {
    const raw = extractRawText(SELECTOR_TEMPLATE_SKU);
    return extractNormalizedSku(raw, { stripSkuPrefix: true });
  }

  // URLから現在のパラメータ値を取得（常に最新のURLを参照）
  function getCurrentParamsFromUrl() {
    try {
      const url = new URL(window.location.href);
      return {
        sku: url.searchParams.get(SKU_PARAM_KEY) || '',
        template: url.searchParams.get(TEMPLATE_PARAM_KEY) || '',
        product: url.searchParams.get(PRODUCT_PARAM_KEY) || '',
        variantOptions: url.searchParams.get(VARIANT_OPTIONS_PARAM_KEY) || ''
      };
    } catch (e) {
      return { sku: '', template: '', product: '', variantOptions: '' };
    }
  }

  // パラメータの優先順位を決定するヘルパー
  // 戻り値: { value: 最終的な値, needsUpdate: URL更新が必要か }
  function resolveParamValue(domValue, urlValue) {
    if (domValue && domValue !== urlValue) {
      // DOMに値があり、URLと異なる → DOMの値でURL更新
      return { value: domValue, needsUpdate: true };
    } else if (urlValue) {
      // DOMが空またはURLと同じ → URLの値を保持
      return { value: urlValue, needsUpdate: false };
    } else if (domValue) {
      // URLにもなく、DOMにある → DOMの値を使用
      return { value: domValue, needsUpdate: false };
    }
    return { value: '', needsUpdate: false };
  }

  // ========================================
  // 内部ステート管理
  // ========================================

  // 内部ステートをURLから初期化し、必要に応じてDOMの値でURLを更新
  function initializeInternalState() {
    const urlParams = getCurrentParamsFromUrl();
    const domSku = getProductSkuFromDom();
    const domTemplate = getTemplateSkuFromDom();
    
    const url = new URL(window.location.href);
    let needsUrlUpdate = false;
    
    // SKUの処理
    const skuResult = resolveParamValue(domSku, urlParams.sku);
    currentSku = skuResult.value;
    if (skuResult.needsUpdate) {
      url.searchParams.set(SKU_PARAM_KEY, skuResult.value);
      needsUrlUpdate = true;
    }
    
    // TEMPLATEの処理
    const templateResult = resolveParamValue(domTemplate, urlParams.template);
    currentTemplate = templateResult.value;
    if (templateResult.needsUpdate) {
      url.searchParams.set(TEMPLATE_PARAM_KEY, templateResult.value);
      needsUrlUpdate = true;
    }
    
    // productは常にURLの値を保持（DOMには表示されないため）
    currentProduct = urlParams.product;
    
    // variantOptionsも常にURLの値を保持（DOMには表示されないため）
    currentVariantOptions = urlParams.variantOptions;
    
    if (needsUrlUpdate) {
      const newUrl = buildUrlString(url);
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }

  // バリアント変更を検知してURLを更新する
  // 変更があった場合のみURLを書き換える
  function syncUrlOnVariantChange(variant) {
    try {
      // DOMから最新の値を取得
      const newSku = getProductSkuFromDom();
      const newTemplate = getTemplateSkuFromDom();
      const newVariantOptions = variant ? getVariantOptionsString(variant) : '';
      
      // 内部ステートから現在の値を取得（URLが書き換えられている可能性があるため）
      const prevSku = currentSku;
      const prevTemplate = currentTemplate;
      const prevProduct = currentProduct;
      const prevVariantOptions = currentVariantOptions;
      
      // 変更があったかチェック（DOMに値がある場合のみ）
      const skuChanged = newSku && newSku !== prevSku;
      const templateChanged = newTemplate && newTemplate !== prevTemplate;
      const variantOptionsChanged = newVariantOptions && newVariantOptions !== prevVariantOptions;
      
      if (!skuChanged && !templateChanged && !variantOptionsChanged) {
        return false;
      }

      // URLを構築: 内部ステートから全パラメータを復元
      const url = new URL(window.location.href);
      
      // 既存パラメータの復元
      if (prevSku) url.searchParams.set(SKU_PARAM_KEY, prevSku);
      if (prevTemplate) url.searchParams.set(TEMPLATE_PARAM_KEY, prevTemplate);
      if (prevProduct) url.searchParams.set(PRODUCT_PARAM_KEY, prevProduct);
      if (prevVariantOptions) url.searchParams.set(VARIANT_OPTIONS_PARAM_KEY, prevVariantOptions);
      
      // 変更があった値のみ更新
      if (skuChanged) {
        url.searchParams.set(SKU_PARAM_KEY, newSku);
        currentSku = newSku;
      }
      
      if (templateChanged) {
        url.searchParams.set(TEMPLATE_PARAM_KEY, newTemplate);
        currentTemplate = newTemplate;
      }

      if (variantOptionsChanged) {
        url.searchParams.set(VARIANT_OPTIONS_PARAM_KEY, newVariantOptions);
        currentVariantOptions = newVariantOptions;
      }

      // URLを書き換え
      const newUrl = buildUrlString(url);
      window.history.replaceState(window.history.state, '', newUrl);
      
      return true;
    } catch (e) {
      return false;
    }
  }

  // ========================================
  // 選択情報表示
  // ========================================

  // 選択中の商品とバリアントオプションを表示
  function updateSelectionInfo() {
    const infoElement = document.querySelector(SELECTOR_SELECTION_INFO);
    if (!infoElement) return;
    
    // templateパラメータがある場合は表示しない
    if (currentTemplate) {
      infoElement.innerHTML = '';
      return;
    }
    
    const product = currentProduct;
    const variantOptions = currentVariantOptions;
    
    if (!product && !variantOptions) {
      infoElement.innerHTML = '';
      return;
    }
    
    let displayText = '';
    let linkTag = '<a href="/collections/base-products" data-skip-product-param>[変更]</a>';

    displayText = `${product} ${linkTag}`;
    // TODO: 商品の variantOptions をセットで表示したいが、テンプレート商品のバリアント選択時に上書きされる問題があるので保留
    if (product && variantOptions) {
      displayText = `${product} / ${variantOptions} ${linkTag}`;
    } else if (product) {
      displayText = `${product} ${linkTag}`;
    } else if (variantOptions) {
      displayText = `${variantOptions}`;
    }

    infoElement.innerHTML = `${displayText}`;
  }

  // ========================================
  // リンク書き換え処理
  // ========================================

  // リンク書き換え用に内部ステートを更新
  function updateInternalStateForLinks() {
    const params = getCurrentParamsFromUrl();
    currentSku = params.sku;
    currentTemplate = params.template;
    currentProduct = params.product;
    currentVariantOptions = params.variantOptions;
  }

  // ステッパー・テンプレート・全リンクをまとめて最新SKU等で書き換える
  function rewriteAllLinksWithCurrentSku() {
    updateInternalStateForLinks();
    rewriteStepperLinks();
    rewriteAllLinks();
    rewriteCreatorCollectionListLinks();
    updateSelectionInfo();
  }

  // mailto等の特殊リンクを除外し、書き換え対象となるナビゲーションリンクを判定する
  function isNavigableAnchor(anchor) {
    if (!anchor) return false;
    const href = anchor.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return false;
    if (href.startsWith('mailto:') || href.startsWith('tel:')) return false;
    return true;
  }

  // 任意のリンクURLにSKU等を付与し、現在ページのクエリと矛盾しないよう調整する
  function buildRelativeUrlWithSku(href, sku, template, product, variantOptions) {
    try {
      const currentUrl = new URL(window.location.href);
      const targetUrl = new URL(href, window.location.origin);
      
      // 新しいパラメータセットを構築（優先順位: 現在のURL < リンク先 < 引数）
      const newParams = new URLSearchParams();

      // 1. 現在のURLの全パラメータをコピー
      currentUrl.searchParams.forEach((value, key) => newParams.set(key, value));

      // 2. リンク先に既にあるパラメータで上書き
      targetUrl.searchParams.forEach((value, key) => newParams.set(key, value));

      // 3. 引数で渡された値があれば最終上書き
      const normalizedSku = normalizeParam(sku);
      const normalizedTemplate = normalizeParam(template);
      const normalizedProduct = normalizeParam(product);
      const normalizedVariantOptions = normalizeParam(variantOptions);

      if (normalizedSku) newParams.set(SKU_PARAM_KEY, normalizedSku);
      if (normalizedTemplate) newParams.set(TEMPLATE_PARAM_KEY, normalizedTemplate);
      if (normalizedProduct) newParams.set(PRODUCT_PARAM_KEY, normalizedProduct);
      if (normalizedVariantOptions) newParams.set(VARIANT_OPTIONS_PARAM_KEY, normalizedVariantOptions);

      // 最終的なURLを構築
      targetUrl.search = newParams.toString();
      return formatUrl(targetUrl, href);
    } catch (e) {
      return href;
    }
  }

  // クエリを削除しつつ、デザインモード（管理画面プレビュー）では必要なキーだけ保持する
  function stripAllQueryParams(href) {
    try {
      const url = new URL(href, window.location.origin);
      
      // Shopifyデザインモードでは必要なパラメータを保持
      if (window.Shopify && window.Shopify.designMode) {
        const current = new URL(window.location.href);
        const params = new URLSearchParams();
        current.searchParams.forEach((value, key) => {
          if (!PARAM_KEYS_TO_SKIP_IN_EDITOR.has(key)) {
            params.set(key, value);
          }
        });
        url.search = params.toString();
      } else {
        url.search = '';
      }
      
      return formatUrl(url, href);
    } catch (e) {
      const idx = href.indexOf('?');
      return idx === -1 ? href : href.slice(0, idx);
    }
  }

  // viewパラメータのみを除去したURLを返す
  function removeViewParam(href) {
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete(VIEW_PARAM_KEY);
      return formatUrl(url, href);
    } catch (e) {
      return href;
    }
  }

  // ステッパー内リンクのクエリを初期化し、整合の取れた状態にする
  function rewriteStepperLinks() {
    const nav = document.querySelector(SELECTOR_STEPPER);
    if (!nav) return;
    const anchors = nav.querySelectorAll('a[href]');
    anchors.forEach((a) => {
      if (!isNavigableAnchor(a)) return;
      if (a.hasAttribute(ATTR_SKIP_PARAM)) return;
      const href = a.getAttribute('href');
      const cleaned = stripAllQueryParams(href);
      a.setAttribute('href', cleaned);
    });
  }

  // ページ全体のリンクに対してSKU/テンプレートを適用し、テンプレート領域内のみview除去も行う
  function rewriteAllLinks() {
    const anchors = document.querySelectorAll('a[href]');
    anchors.forEach((a) => {
      if (!isNavigableAnchor(a)) return;
      if (a.hasAttribute(ATTR_SKIP_PARAM)) return;
      if (a.closest(SELECTOR_STEPPER)) return; // ステッパー内リンクは除外
      
      const href = a.getAttribute('href');
      if (a.closest(SELECTOR_STEP_TEMPLATE)) {
        a.setAttribute('href', removeViewParam(buildRelativeUrlWithSku(href, currentSku, currentTemplate, currentProduct, currentVariantOptions)));
      } else {
        a.setAttribute('href', buildRelativeUrlWithSku(href, currentSku, currentTemplate, currentProduct, currentVariantOptions));
      }
    });
  }

  // クリエイターコレクション一覧のリンクにSKU等を付与し、専用テンプレートへ誘導する
  function rewriteCreatorCollectionListLinks() {
    const container = document.getElementById(ID_CREATOR_COLLECTION_LIST);
    if (!container) return;
    const anchors = container.querySelectorAll('a[href]');
    anchors.forEach((a) => {
      if (!isNavigableAnchor(a)) return;
      if (a.hasAttribute(ATTR_SKIP_PARAM)) return;
      const href = a.getAttribute('href');
      try {
        const withSku = buildRelativeUrlWithSku(href, currentSku, currentTemplate, currentProduct, currentVariantOptions);
        const url = new URL(withSku, window.location.origin);
        url.searchParams.set(VIEW_PARAM_KEY, VIEW_STEP_CREATOR_PRODUCTS);
        a.setAttribute('href', formatUrl(url, href));
      } catch (e) {}
    });
  }

  // クリック直前にリンク先を書き換え、最新SKU等を反映した状態で遷移させる
  function onDocumentClick(e) {
    const anchor = e.target && (e.target.closest('a') || null);
    if (!anchor) return;
    if (!isNavigableAnchor(anchor)) return;
    if (anchor.hasAttribute(ATTR_SKIP_PARAM)) return;
    
    // URLから最新の値を取得
    updateInternalStateForLinks();
    
    const href = anchor.getAttribute('href');
    let updated;
    if (anchor.closest(SELECTOR_STEPPER)) {
      updated = stripAllQueryParams(href);
    } else if (anchor.closest(SELECTOR_STEP_TEMPLATE)) {
      const withSku = buildRelativeUrlWithSku(href, currentSku, currentTemplate, currentProduct, currentVariantOptions);
      updated = removeViewParam(withSku);
    } else {
      updated = buildRelativeUrlWithSku(href, currentSku, currentTemplate, currentProduct, currentVariantOptions);
    }
    if (updated !== anchor.getAttribute('href')) {
      anchor.setAttribute('href', updated);
    }
  }

  // ========================================
  // イベント処理
  // ========================================

  // バリアント変更イベントを監視し、URL更新とリンク書換えを実行
  function subscribeVariantChanges() {
    try {
      if (typeof subscribe !== 'function' || 
          typeof PUB_SUB_EVENTS === 'undefined' || 
          !PUB_SUB_EVENTS.variantChange) {
        return;
      }
      
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        // 重複実行を防ぐ
        if (isProcessingVariantChange) {
          return;
        }
        
        isProcessingVariantChange = true;
        
        // イベントデータからバリアント情報を取得
        const variant = event && event.data && event.data.variant ? event.data.variant : null;
        
        const processVariantChange = () => {
          try {
            // URLを更新（変更があった場合のみ）
            syncUrlOnVariantChange(variant);
            
            // リンクを書き換え（常に実行）
            rewriteAllLinksWithCurrentSku();
          } finally {
            isProcessingVariantChange = false;
          }
        };
        
        // 即座に処理
        processVariantChange();
        
        // DOM更新後にも再実行（念のため）
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => {
            if (!isProcessingVariantChange) {
              processVariantChange();
            }
          });
        }
      });
    } catch (e) {
      // エラーを無視
    }
  }

  // ========================================
  // 初期化
  // ========================================

  // 初期化ハンドラで各種同期処理とイベント購読を一括設定
  function init() {
    try {
      // 内部ステートをURLから初期化
      initializeInternalState();
      
      // ステッパー内リンクのクエリを初期化
      rewriteStepperLinks();
      
      // 全リンクを書き換え（選択情報も更新される）
      rewriteAllLinksWithCurrentSku();
      
      // バリアント変更イベントを購読
      subscribeVariantChanges();
      
      // クリックイベントをリッスン
      document.addEventListener('click', onDocumentClick, true);
    } catch (e) {
      // エラーを無視
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
